import type { BaseAPI } from 'src/portal-bff-api';

import { useMemo } from 'react';

import { CONFIG } from 'src/global-config';
import { Configuration } from 'src/portal-bff-api';
import fetchOverride from 'src/lib/fetch-override';

import { useAuthContext } from 'src/auth/hooks';
import { isValidToken } from 'src/auth/context/amplify/utils';

// ----------------------------------------------------------------------

const useApiConfig = () => {
  const { user } = useAuthContext();

  return useMemo(
    () =>
      new Configuration({
        basePath: CONFIG.serverUrl,
        fetchApi: fetchOverride,
        accessToken: user?.idToken && isValidToken(user.idToken) ? user.idToken : undefined,
      }),
    [user?.idToken]
  );
};

const useWithPreMiddleware = <T extends BaseAPI>(api: T) => {
  const { user } = useAuthContext();

  const authenticatedApi = useMemo(() => {
    if (!user?.idToken || !isValidToken(user.idToken)) {
      return api;
    }

    return api.withPreMiddleware(async (context) => {
      const headers = new Headers(context.init.headers);
      headers.set('Authorization', `Bearer ${user.idToken}`);
      context.init.headers = headers;
      return context;
    });
  }, [api, user?.idToken]);

  return {
    api: authenticatedApi,
    isAuthenticated: Boolean(user?.idToken && isValidToken(user.idToken)),
  };
};

// ----------------------------------------------------------------------

export { useApiConfig, useWithPreMiddleware };
